import AuthSplitCard from './auth/AuthSplitCard'
import React, { useState, useEffect } from 'react'
import { Box, CircularProgress, Typography, Button, Alert, Link } from '@mui/material'
import { motion } from 'framer-motion'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { PasswordInput } from './Inputs'
import { useAppSelector } from '../hooks'
import { getServerBaseUrl } from '@/utils/serverUrl'
import './AuthenticationDialog.scss'

/**
 * This page sits on a light card, and the eye is the one control on it that has to read
 * against that rather than against the in-game chrome. Kept here rather than pushed into
 * `PasswordInput` because it is this screen's look, not the component's.
 */
const TOGGLE_SX = {
  color: 'var(--sevv-text-secondary, #757575)',
  '&:hover': {
    color: 'var(--sevv-primary, #f97415)',
    backgroundColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 10%, transparent)',
  },
} as const

interface PasswordResetPageProps {
  token: string | null
  onSuccess?: () => void
}

export default function PasswordResetPage({ token, onSuccess }: PasswordResetPageProps) {
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isValidatingToken, setIsValidatingToken] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const [confirmPasswordError, setConfirmPasswordError] = useState<string | null>(null)
  const [tokenValid, setTokenValid] = useState(false)
  const [logoLoaded, setLogoLoaded] = useState(false)
  
  const settings = useAppSelector((state) => state.workspace.settings)
  const platformName = settings?.platform_name || 'Sevv'
  const logoUrl = '/assets/images/SVGLogo.svg' // Always use SEVV logo

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('No reset token provided. Please use the link from your email.')
        setIsValidatingToken(false)
        setTokenValid(false)
        return
      }

      setIsValidatingToken(true)
      setError(null)

      try {
        const baseUrl = getServerBaseUrl()
        const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
        const fullUrl = isDev 
          ? '/api/auth/password-reset/verify' 
          : `${baseUrl}/api/auth/password-reset/verify`

        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token }),
        })

        const data = await response.json()

        if (data.success) {
          setTokenValid(true)
        } else {
          setError(data.error || 'Invalid or expired reset token. Please request a new password reset link.')
          setTokenValid(false)
        }
      } catch (err: any) {
        console.error('Token validation error:', err)
        setError('Failed to validate reset token. Please try again or request a new password reset link.')
        setTokenValid(false)
      } finally {
        setIsValidatingToken(false)
      }
    }

    validateToken()
  }, [token])

  const validatePassword = (pwd: string): string | null => {
    if (!pwd) {
      return 'Password is required'
    }
    if (pwd.length < 8) {
      return 'Password must be at least 8 characters long'
    }
    return null
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value
    setPassword(newPassword)
    setPasswordError(validatePassword(newPassword))
    
    // Also validate confirm password if it's been entered
    if (confirmPassword) {
      if (newPassword !== confirmPassword) {
        setConfirmPasswordError('Passwords do not match')
      } else {
        setConfirmPasswordError(null)
      }
    }
  }

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newConfirmPassword = e.target.value
    setConfirmPassword(newConfirmPassword)
    
    if (newConfirmPassword !== password) {
      setConfirmPasswordError('Passwords do not match')
    } else {
      setConfirmPasswordError(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setPasswordError(null)
    setConfirmPasswordError(null)

    // Validate passwords
    const pwdError = validatePassword(password)
    if (pwdError) {
      setPasswordError(pwdError)
      return
    }

    if (password !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const baseUrl = getServerBaseUrl()
      const isDev = !!((import.meta as any).env && (import.meta as any).env.DEV)
      const fullUrl = isDev 
        ? '/api/auth/password-reset/complete' 
        : `${baseUrl}/api/auth/password-reset/complete`

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword: password }),
      })

      const data = await response.json()

      if (data.success) {
        setSuccess(true)
        
        // Call onSuccess callback if provided
        if (onSuccess) {
          setTimeout(() => {
            onSuccess()
          }, 2000)
        }
      } else {
        setError(data.error || 'Failed to reset password. The link may have expired or is invalid.')
      }
    } catch (err: any) {
      console.error('Password reset error:', err)
      setError(err.message || 'Failed to reset password. Please try again or request a new password reset link.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSignInClick = () => {
    // Clear URL parameters and redirect to home
    window.history.replaceState({}, document.title, '/')
    window.location.reload()
  }

  const handleRequestNewLink = () => {
    // Redirect to home where user can request a new password reset
    window.history.replaceState({}, document.title, '/')
    window.location.reload()
  }

  // Show loading state while validating token
  if (isValidatingToken) {
    return (
      <AuthSplitCard screen="signIn">
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Logo Container */}
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              sx={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                borderRadius: '12px',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                  opacity: 0.8
                }
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={`${platformName} Logo`}
                onLoad={() => setLogoLoaded(true)}
                sx={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Box>
          <CircularProgress sx={{ mb: 2, color: 'var(--sevv-primary, #f97415)' }} />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'var(--sevv-text-primary, #424242)' }}>
            Validating Reset Link
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--sevv-text-secondary, #757575)' }}>
            Please wait while we verify your password reset link...
          </Typography>
        </Box>
      </AuthSplitCard>
    )
  }

  // Show error state if token is invalid
  if (!tokenValid && !isValidatingToken) {
    return (
      <AuthSplitCard screen="signIn">
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Logo Container */}
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
              mt: 2
            }}
          >
            <Box
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              sx={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                borderRadius: '12px',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                  opacity: 0.8
                }
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={`${platformName} Logo`}
                onLoad={() => setLogoLoaded(true)}
                sx={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Box>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'var(--sevv-error, #e74c3c)', mb: 2, mx: 'auto' }} />
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'var(--sevv-text-primary, #424242)' }}>
            Invalid Reset Link
          </Typography>
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              backgroundColor: 'color-mix(in srgb, var(--sevv-error, #e74c3c) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--sevv-error, #e74c3c) 30%, transparent)',
              color: 'var(--sevv-error, #e74c3c)',
              borderRadius: '8px',
              '& .MuiAlert-icon': {
                color: 'var(--sevv-error, #e74c3c)'
              }
            }}
          >
            {error || 'This password reset link is invalid or has expired. Please request a new password reset link.'}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleRequestNewLink}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
              color: '#ffffff !important',
              boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
              '&:hover': {
                background: 'linear-gradient(135deg, var(--sevv-primary-dark, #ea580c) 0%, var(--sevv-primary, #f97415) 100%)',
                boxShadow: '0 10px 25px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
                transform: 'translateY(-1px)',
                color: '#ffffff !important'
              }
            }}
          >
            Request New Reset Link
          </Button>
        </Box>
      </AuthSplitCard>
    )
  }

  if (success) {
    return (
      <AuthSplitCard screen="signIn">
        <Box
          component={motion.div}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          sx={{
            width: '100%',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          {/* Logo Container */}
          <Box 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 3
            }}
          >
            <Box
              component={motion.div}
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              sx={{
                width: '60px',
                height: '60px',
                background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                borderRadius: '12px',
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                  opacity: 0.8
                }
              }}
            >
              <Box
                component="img"
                src={logoUrl}
                alt={`${platformName} Logo`}
                onLoad={() => setLogoLoaded(true)}
                sx={{
                  width: '40px',
                  height: '40px',
                  objectFit: 'contain',
                  position: 'relative',
                  zIndex: 1,
                  filter: 'brightness(0) invert(1)'
                }}
              />
            </Box>
          </Box>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'var(--sevv-success, #27ae60)', mb: 2 }} />
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600, color: 'var(--sevv-text-primary, #424242)' }}>
            Password Reset Successful!
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'var(--sevv-text-secondary, #757575)' }}>
            Your password has been reset successfully. You can now sign in with your new password.
          </Typography>
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={handleSignInClick}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
              color: '#ffffff !important',
              boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
              '&:hover': {
                background: 'linear-gradient(135deg, var(--sevv-primary-dark, #ea580c) 0%, var(--sevv-primary, #f97415) 100%)',
                boxShadow: '0 10px 25px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
                transform: 'translateY(-1px)',
                color: '#ffffff !important'
              },
              '&:active': {
                color: '#ffffff !important'
              },
              '&:focus': {
                color: '#ffffff !important'
              }
            }}
          >
            Sign In
          </Button>
        </Box>
      </AuthSplitCard>
    )
  }

  return (
    <AuthSplitCard screen="signIn">
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Logo Container */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
            mt: 2
          }}
        >
          <Box
            component={motion.div}
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: logoLoaded ? 1 : 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            sx={{
              width: '60px',
              height: '60px',
              background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
              borderRadius: '12px',
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
                opacity: 0.8
              }
            }}
          >
            <Box
              component="img"
              src={logoUrl}
              alt={`${platformName} Logo`}
              onLoad={() => setLogoLoaded(true)}
              sx={{
                width: '40px',
                height: '40px',
                objectFit: 'contain',
                position: 'relative',
                zIndex: 1,
                filter: 'brightness(0) invert(1)'
              }}
            />
          </Box>
          <Box
            component={motion.div}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            <Typography 
              variant="h4" 
              sx={{ 
                mb: 1, 
                fontWeight: 600, 
                color: 'var(--sevv-text-primary, #424242)',
                fontSize: '1.75rem',
                textAlign: 'center'
              }}
            >
              Reset Your Password
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'var(--sevv-text-secondary, #757575)',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}
            >
              Enter your new password below
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              backgroundColor: 'color-mix(in srgb, var(--sevv-error, #e74c3c) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--sevv-error, #e74c3c) 30%, transparent)',
              color: 'var(--sevv-error, #e74c3c)',
              borderRadius: '8px',
              '& .MuiAlert-icon': {
                color: 'var(--sevv-error, #e74c3c)'
              }
            }}
          >
            <ErrorOutlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}
        >
          <PasswordInput
            variant="outlined"
            fullWidth
            label="New Password"
            showLockIcon
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            toggleSx={TOGGLE_SX}
            value={password}
            onChange={handlePasswordChange}
            error={!!passwordError}
            helperText={passwordError}
            required
            margin="normal"
            sx={{ 
              mb: 2,
              '& .MuiInputLabel-root': {
                color: 'var(--sevv-text-secondary, #757575)'
              },
              '& .MuiOutlinedInput-root': {
                color: 'var(--sevv-text-primary, #424242)',
                '& .MuiInputBase-input': {
                  color: 'var(--sevv-text-primary, #424242)'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--sevv-divider, #eeeeee)',
                  '&:hover': {
                    borderColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 35%, transparent)'
                  },
                  '&.Mui-focused': {
                    borderColor: 'var(--sevv-primary, #f97415)'
                  }
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--sevv-error, #e74c3c)'
                }
              },
              '& .MuiFormHelperText-root': {
                color: passwordError ? 'var(--sevv-error, #e74c3c)' : 'var(--sevv-text-secondary, #757575)'
              }
            }}
          />

          <PasswordInput
            variant="outlined"
            fullWidth
            label="Confirm New Password"
            showLockIcon
            visibilityLabelNoun="password confirmation"
            showPassword={showConfirmPassword}
            onToggleShowPassword={() => setShowConfirmPassword(!showConfirmPassword)}
            toggleSx={TOGGLE_SX}
            value={confirmPassword}
            onChange={handleConfirmPasswordChange}
            error={!!confirmPasswordError}
            helperText={confirmPasswordError}
            required
            margin="normal"
            sx={{ 
              mb: 3,
              '& .MuiInputLabel-root': {
                color: 'var(--sevv-text-secondary, #757575)'
              },
              '& .MuiOutlinedInput-root': {
                color: 'var(--sevv-text-primary, #424242)',
                '& .MuiInputBase-input': {
                  color: 'var(--sevv-text-primary, #424242)'
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--sevv-divider, #eeeeee)',
                  '&:hover': {
                    borderColor: 'color-mix(in srgb, var(--sevv-primary, #f97415) 35%, transparent)'
                  },
                  '&.Mui-focused': {
                    borderColor: 'var(--sevv-primary, #f97415)'
                  }
                },
                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'var(--sevv-error, #e74c3c)'
                }
              },
              '& .MuiFormHelperText-root': {
                color: confirmPasswordError ? 'var(--sevv-error, #e74c3c)' : 'var(--sevv-text-secondary, #757575)'
              }
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isLoading || !password || !confirmPassword || !!passwordError || !!confirmPasswordError}
            sx={{
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '44px',
              background: 'linear-gradient(135deg, var(--sevv-primary, #f97415) 0%, var(--sevv-primary-light, #ffa047) 100%)',
              color: '#ffffff !important',
              boxShadow: '0 4px 14px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
              '&:hover': {
                background: 'linear-gradient(135deg, var(--sevv-primary-dark, #ea580c) 0%, var(--sevv-primary, #f97415) 100%)',
                boxShadow: '0 10px 25px 0 color-mix(in srgb, var(--sevv-primary, #f97415) 40%, transparent)',
                transform: 'translateY(-1px)',
                color: '#ffffff !important'
              },
              '&:active': {
                color: '#ffffff !important'
              },
              '&:focus': {
                color: '#ffffff !important'
              },
              '&:disabled': {
                background: 'color-mix(in srgb, var(--sevv-primary, #f97415) 30%, transparent)',
                color: 'var(--sevv-gray-400, #bdbdbd) !important',
                boxShadow: 'none',
                transform: 'none'
              }
            }}
          >
            {isLoading ? (
              <CircularProgress size={24} sx={{ color: '#ffffff' }} />
            ) : (
              'Reset Password'
            )}
          </Button>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            component="button"
            type="button"
            onClick={handleSignInClick}
            sx={{ 
              textDecoration: 'none', 
              color: 'var(--sevv-primary, #f97415)',
              fontWeight: 500,
              fontSize: '0.875rem',
              cursor: 'pointer',
              '&:hover': {
                color: 'var(--sevv-primary-dark, #ea580c)',
                textDecoration: 'underline'
              }
            }}
          >
            Back to Sign In
          </Link>
        </Box>
      </Box>
    </AuthSplitCard>
  )
}

